import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";
import { invalidateAfterTransaction } from "utils/useSharedTransactionInvalidation";
import type { PatientUpdateRequest } from "api/requests";

export type PatientTransactionOptions = UseMutationOptions<number, Error, PatientUpdateRequest>;

/**
 * Client hook to post a new Patient/Responsible Party transaction.
 * @param options Options for the client call; provides callbacks for when the API call succeeds or fails.
 * @returns React Query mutation properties.
 */
export function usePatientTransaction(options: Omit<PatientTransactionOptions, "mutationFn">) {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccessWrapper = (
        data: number,
        variables: PatientUpdateRequest,
        onMutateResult: unknown,
        context: MutationFunctionContext,
    ) => {
        // Invalidate queries that are affected by posting a transaction
        invalidateAfterTransaction(queryClient, "payment", data, null);

        // Use callback if provided
        return options.onSuccess?.(data, variables, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationFn: async (request: PatientUpdateRequest) => await api.provider.patientTransaction(request),
        onSuccess: onSuccessWrapper,
    });
}
